import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';

import type { RootState, AppDispatch } from 'store/store';

import { checkIsUsernameFree } from 'businessLogic/Auth/Auth';
import { makeDebounce } from 'components/Shared/FrictionlessLogin/debounce';
import { emailRegex } from 'shared/freeTextModalValidators';
import { isIdentitySignInWidgetErrorSelector } from 'store/auth/selectors';
import { useSelector } from 'store/utils';

interface UseUsernameAvailabilityProps {
  email: string;
  shouldCheckAvailability?: boolean;
}

interface UseUsernameAvailabilityReturn {
  isAvailable: boolean | null;
  isLoading: boolean;
  error: string | null;
}

const debounce = makeDebounce();

export const useUsernameAvailability = ({
  email,
  shouldCheckAvailability = true,
}: UseUsernameAvailabilityProps): UseUsernameAvailabilityReturn => {
  const dispatch = useDispatch<AppDispatch>();

  // Access Redux store state
  const config = useSelector((state: RootState) => state.config);
  const i18n = useSelector((state: RootState) => state.i18n);
  const irisToken = useSelector((state: RootState) => state.insight?.irisToken);
  const isIdentitySignInWidgetError = useSelector(isIdentitySignInWidgetErrorSelector);

  // Local state
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for debouncing
  const lastCheckedEmailRef = useRef<string>('');

  const checkUsernameAvailability = useCallback(
    async (emailToCheck: string) => {
      if (
        !shouldCheckAvailability ||
        !emailToCheck.trim() ||
        !emailRegex.test(emailToCheck) ||
        isIdentitySignInWidgetError
      ) {
        setIsAvailable(null);
        setIsLoading(false);
        setError(null);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        // Get the iris token if not already available
        if (!irisToken) {
          setIsLoading(false);
          return;
        }

        lastCheckedEmailRef.current = emailToCheck;

        const available = await checkIsUsernameFree({
          emailAddress: emailToCheck,
          config,
          riskProfileToken: irisToken,
          locale: i18n?.locale || 'en-US',
        });

        setIsAvailable(available);
      } catch (err) {
        setError('Failed to check username availability');
        setIsAvailable(true);
      } finally {
        setIsLoading(false);
      }
    },
    [
      dispatch,
      config,
      i18n?.locale,
      irisToken,
      shouldCheckAvailability,
      isIdentitySignInWidgetError,
    ]
  );

  // Debounced effect for email changes
  useEffect(() => {
    debounce(() => {
      checkUsernameAvailability(email);
    }, 350);
  }, [email, checkUsernameAvailability]);

  return {
    isAvailable,
    isLoading,
    error,
  };
};
